import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus } from '@exam-management/graphql';
import {
  EllipsisText,
  Flex,
  DeleteOutlineIcon,
  MoreMenuIconButton,
  type FlexProps,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { useQConfigStructureModal } from '../../providers';
import type { QConfigStructureData } from '../../types';
import { QConfigStructureNoRowsOverlay } from '../qconfig-structure-empty-state';
import { QConfigStructureTable } from '../qconfig-structure-table';

import { EmptyPartSection, PartSection } from './part-section';
import { formatQConfigStructureData } from './utils';

interface QConfigStructureListProps extends FlexProps {
  data: QConfigStructureData[];
}

export default function QConfigStructureList(props: Readonly<QConfigStructureListProps>) {
  const { data, ...restProps } = props;
  const { qbsList, partList, tableData } = formatQConfigStructureData(data);
  const { qConfigData } = useQConfig();

  if (qbsList.length === 0) {
    return <QConfigStructureNoRowsOverlay />;
  }

  return (
    <Flex
      {...restProps}
      sx={{
        flexDirection: 'column',
        gap: (theme) => theme.spacing(6),
        ...restProps.sx,
      }}
    >
      {qbsList.map((qbs) => {
        const parts = partList.get(qbs.id) ?? [];
        const hasNoParts = parts.length === 0;
        const courseId = qbs.id;

        const { setAction } = useQConfigStructureModal();

        const handleRemove = () => {
          setAction({
            action: 'QCONFIG_DELETE',
            payload: { qConfigStructureId: courseId },
          });
        };

        const moreQbsOptions: MoreMenuIconButtonOption[] = [
          {
            icon: (
              <DeleteOutlineIcon
                sx={{
                  color: (theme) => theme.palette.error.main,
                }}
              />
            ),
            label: 'Remove',
            onClick: handleRemove,
            sx: {
              ':hover': {
                backgroundColor: (theme) => theme.palette.primary.lighter,
              },
              color: (theme) => theme.palette.error.main,
            },
          },
        ];

        return (
          <Flex
            key={qbs.id}
            sx={{
              flexDirection: 'column',
              gap: (theme) => theme.spacing(4),
            }}
          >
            <Flex
              sx={(theme) => ({
                bgcolor: theme.palette.common.black,
                color: theme.palette.common.white,
                p: theme.spacing(2.5, 4),
                borderRadius: theme.shape.rounded(2),
                minHeight: 56,
                alignItems: 'center',
              })}
            >
              <EllipsisText
                variant='h4'
                sx={{
                  flex: 1,
                }}
              >
                {qbs.name}
              </EllipsisText>
              <WithPermission requiredPermission={EDIT_QCONFIG_REQUIRED_PERMISSIONS}>
                {qConfigData.status === QConfigStatus.Draft ? (
                  <MoreMenuIconButton
                    options={moreQbsOptions}
                    slotProps={{
                      iconButton: { sx: { color: 'inherit' } },
                    }}
                  />
                ) : null}
              </WithPermission>
            </Flex>
            {hasNoParts ? (
              <EmptyPartSection />
            ) : (
              parts.map((part) => {
                const items = tableData.get(part.id) ?? [];

                return (
                  <PartSection key={part.id} partData={part}>
                    <QConfigStructureTable
                      data={items}
                      sx={{
                        mt: 3,
                      }}
                    />
                  </PartSection>
                );
              })
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}
